<script setup>
import {ref, onMounted, onUnmounted, watch, computed} from 'vue'
import { Clapperboard, Sparkles, Flame, Clock, Trophy, Film, Video, CheckCircle } from 'lucide-vue-next'
import axios from 'axios'
import { useRecordSgaGoal } from '@/composables/useRecordSgaGoal'
import {getFathomPagePrefix, trackFathomEvent} from "@/Helpers/analytics";
import {Link, router, usePage} from "@inertiajs/vue3";

const props = defineProps({
    castings: {
        type: Array,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    hasMore: {
        type: Boolean,
        default: false,
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false,
    },
    routePrefix: {
        type: String,
        default: 'members2',
    },
    userBalance: {
        type: Number,
        default: 0,
    },
    sort: {
        type: String,
        default: 'trending',
    },
    supportCost: {
        type: Number,
        default: 1,
    },
})

const sharedProps = usePage().props

const requiresAgeVerification = computed(() => !!(sharedProps.requiresAgeVerification))

const emit = defineEmits(['loadMore', 'joinClick', 'updateSort', 'openSupportModal', 'updateBalance', 'openBuyStars'])

// Local reactive star counts so UI updates after giving
const localStars = ref({})  // casting_id -> stars_received
const localGiven = ref({})  // casting_id -> user_stars_given

const starsReceived = (casting) => localStars.value[casting.id] ?? casting.stars_received
const userGiven = (casting)    => localGiven.value[casting.id]  ?? casting.user_stars_given

const thumbsLoading = ref({})  // casting_id -> bool
const confirmingId = ref(null) // casting_id awaiting confirmation
const supportError = ref({})   // casting_id -> error message

// NOTE: thumbs-up (star cost) button is intentionally hidden — upvotes are now free via the vote buttons below
const requestThumbsUp = (casting) => {
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_Support_Casting_Button_Clicked')

    if (!props.isUserLoggedIn) {
        emit('joinClick')
        return
    }
    if (isExpired(casting) || userGiven(casting) > 0 || thumbsLoading.value[casting.id]) return

    if (props.supportCost === -1) {
        emit('openSupportModal', casting)
        return
    }

    confirmingId.value = casting.id
}

const confirmThumbsUp = async (casting) => {
    supportError.value[casting.id] = null
    thumbsLoading.value[casting.id] = true

    // Optimistic update
    localStars.value[casting.id] = starsReceived(casting) + 1
    localGiven.value[casting.id] = 1

    try {
        const response = await axios.post(route('members2.reels.casting.support', { id: casting.id }))
        localStars.value[casting.id] = response.data.stars_received
        confirmingId.value = null

        // Emit event to update parent's user balance
        if (response.data.remaining_balance !== undefined) {
            emit('updateBalance', response.data.remaining_balance)
        }
    } catch (e) {
        // Roll back
        localStars.value[casting.id] = starsReceived(casting) - 1
        localGiven.value[casting.id] = 0
        supportError.value[casting.id] = e.response?.data?.message || 'Something went wrong.'
    } finally {
        thumbsLoading.value[casting.id] = false
    }
}

const markSupported = (castingId, starsReceived) => {
    localStars.value[castingId] = starsReceived
    localGiven.value[castingId] = 1
}

defineExpose({ markSupported })

// ── Voting ────────────────────────────────────────────────────────────────────
const { recordSgaGoal } = useRecordSgaGoal()
const localVote     = ref({})  // casting_id -> -1 | 0 | 1
const localNetScore = ref({})  // casting_id -> number
const voteLoading   = ref({})  // casting_id -> bool
const voteError     = ref({})  // casting_id -> error message

const userVote  = (casting) => localVote.value[casting.id]     ?? casting.user_vote     ?? 0
const netScore  = (casting) => localNetScore.value[casting.id] ?? casting.net_score     ?? 0

const submitVote = async (casting, voteValue) => {
    const event = voteValue === 1 ? 'Boost' : 'Not_For_Me';
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_' + event + '_Button_Clicked')

    if (!props.isUserLoggedIn) {
        emit('joinClick', 'Castings_List_' + event)
        return
    }
    if (voteLoading.value[casting.id] || casting.is_own) return

    voteError.value[casting.id] = null

    const prevVote     = userVote(casting)
    const prevNetScore = netScore(casting)
    // Toggle off if same vote, otherwise apply new vote
    const newVote      = prevVote === voteValue ? 0 : voteValue
    const delta        = newVote - prevVote
    // Optimistic update
    localVote.value[casting.id]     = newVote
    localNetScore.value[casting.id] = prevNetScore + delta
    voteLoading.value[casting.id]   = true

    try {
        const response = await axios.post(route('members2.reels.casting.vote', { id: casting.id }), { vote: voteValue })
        localVote.value[casting.id]     = response.data.user_vote
        localNetScore.value[casting.id] = response.data.net_score

        if (response.data.remaining_balance !== undefined) {
            emit('updateBalance', response.data.remaining_balance)
        }

        if (response.data.user_vote !== 0) {
            recordSgaGoal('vote_pitch', { casting_id: casting.id })
        }
    } catch (e) {
        // Roll back
        localVote.value[casting.id]     = prevVote
        localNetScore.value[casting.id] = prevNetScore
        voteError.value[casting.id] = e.response?.data?.message || 'Something went wrong.'
        if (e.response?.data?.message?.includes('enough Stars')) {
            emit('openBuyStars')
        }
    } finally {
        voteLoading.value[casting.id] = false
    }
}

const onDocClick = (e) => {
    if (confirmingId.value && !e.target.closest('.relative')) {
        supportError.value[confirmingId.value] = null
        confirmingId.value = null
    }
}

// Infinite scroll
const sentinel = ref(null)
let observer = null

const setupObserver = () => {
    if (observer) observer.disconnect()
    if (!sentinel.value) return
    observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && props.hasMore && !props.loading) {
            emit('loadMore')
        }
    }, { rootMargin: '200px' })
    observer.observe(sentinel.value)
}

onMounted(() => {
    document.addEventListener('click', onDocClick, true)
    setupObserver()
})

onUnmounted(() => {
    document.removeEventListener('click', onDocClick, true)
    observer?.disconnect()
})

watch(sentinel, setupObserver)

const formatExpiry = (isoDate) => {
    if (!isoDate) return null
    const diff = new Date(isoDate) - Date.now()
    if (diff <= 0) return 'Expired'
    const days = Math.floor(diff / 86400000)
    if (days > 0) return `${days}d left`
    const hours = Math.floor(diff / 3600000)
    return `${hours}h left`
}

// Castings do not expire for now
// const isExpired = (casting) => casting.status === 3 || (!!casting.expires_at && new Date(casting.expires_at) <= Date.now())
const isExpired = (casting) => false

const formatExpiredOn = (casting) => {
    if (!isExpired(casting)) return null
    if (casting.expires_at && new Date(casting.expires_at) <= Date.now()) {
        const date = new Date(casting.expires_at)
        const diff = Date.now() - date
        const days = Math.floor(diff / 86400000)
        if (days === 0) return 'Expired today'
        if (days === 1) return 'Expired yesterday'
        if (days < 30) return `Expired ${days}d ago`
        const months = Math.floor(days / 30)
        if (months < 12) return `Expired ${months}mo ago`
        return `Expired ${Math.floor(months / 12)}y ago`
    }
    return 'Expired'
}
const isDraft = (casting) => casting.status === 0
const showDraft = (casting) => props.isUserLoggedIn && isDraft(casting) && casting.is_own

const formatLabel = (format) => {
    const map = { VR: 'VR', 'Mixed Reality': 'Mixed Reality', Vertical: 'Vertical', Standard: 'Standard' }
    return map[format] ?? format
}

const expandedDescriptions = ref({})  // casting_id -> bool

const toggleDescription = (castingId) => {
    expandedDescriptions.value[castingId] = !expandedDescriptions.value[castingId]
}

const sortOptions = [
    { value: 'trending' },
    { value: 'newest' },
    { value: 'most_supported', hidden: true },
    { value: 'now_casting' },
    { value: 'in_the_race' },
    { value: 'in_production' },
    { value: 'released' },
    { value: 'expired', hidden: true },
]

const handleSortClick = (sort) => {
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_' + sort + '_Sort_Castings_Clicked')
    emit('updateSort', sort)
}

const handleCastingClick = (e, casting) => {
    if (e.target.closest('a, button, input, textarea')) return
    if (!casting.slug) return
    router.visit(route(props.routePrefix + '.studio.casting.view', { slug: casting.slug }))
}

const handleSubmitPitchClick = () => {
    trackFathomEvent(getFathomPagePrefix(props.routePrefix) + '_Submit_Pitch_Empty_State_Clicked')
    router.visit(route(props.routePrefix + '.studio.create-scene'))
}
</script>

<template>
    <div class="min-h-screen bg-gray-100 dark:bg-zinc-950">
<div class="max-w-2xl mx-auto px-4 sm:px-6 py-6">

        <!-- Loading skeleton -->
        <div v-if="loading && castings.length === 0" class="space-y-3">
            <div
                v-for="n in 5"
                :key="n"
                class="bg-zinc-900 rounded-2xl border border-white/[0.06] p-5 animate-pulse space-y-3"
            >
                <div class="h-5 bg-zinc-800 rounded w-1/2" />
                <div class="h-3 bg-zinc-800 rounded w-1/4" />
                <div class="h-3 bg-zinc-800 rounded w-3/4" />
                <div class="h-3 bg-zinc-800 rounded w-2/3" />
            </div>
        </div>

        <!-- Vertical list / empty state -->
        <template v-else>
            <!-- Sort bar -->
            <div class="flex items-center gap-1 sm:gap-1.5 mb-4 overflow-x-auto -mx-1 px-1 scrollbar-hide">
                <button
                    v-for="option in sortOptions"
                    :key="option.value"
                    @click="handleSortClick(option.value)"
                    v-show="!option.hidden"
                    :class="[
                        'flex items-center gap-0.5 sm:gap-1 px-2 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold transition-colors whitespace-nowrap flex-shrink-0',
                        sort === option.value
                            ? 'bg-blue-600/40 text-blue-300 border border-blue-500/40'
                            : 'bg-white/[0.06] text-white/50 hover:bg-white/[0.1] hover:text-white/80',
                    ]"
                >
                    <template v-if="option.value === 'trending'">
                        <Flame class="hidden sm:block w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                        Trending
                    </template>
                    <template v-else-if="option.value === 'most_supported'">
                        <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="hidden sm:block w-3.5 h-3.5 flex-shrink-0" />
                        Most Supported
                    </template>
                    <template v-else-if="option.value === 'newest'">
                        <Sparkles class="hidden sm:block w-3.5 h-3.5 flex-shrink-0" />
                        Newest
                    </template>
                    <template v-else-if="option.value === 'now_casting'">
                        <Video class="hidden sm:block w-3.5 h-3.5 text-sky-400 flex-shrink-0" />
                        Now Casting
                    </template>
                    <template v-else-if="option.value === 'in_the_race'">
                        <Trophy class="hidden sm:block w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
                        In the Race
                    </template>
                    <template v-else-if="option.value === 'in_production'">
                        <Film class="hidden sm:block w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                        In Production
                    </template>
                    <template v-else-if="option.value === 'released'">
                        <CheckCircle class="hidden sm:block w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                        Released
                    </template>
                    <!--<template v-else-if="option.value === 'expired'">
                        <Clock class="hidden sm:block w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                        Expired
                    </template>-->
                </button>
            </div>

            <!-- Empty state -->
            <div v-if="!loading && castings.length === 0" class="text-center py-20">
                <Clapperboard class="w-12 h-12 text-white/20 mx-auto mb-4" />
                <template v-if="sort === 'now_casting'">
                    <h3 class="text-lg font-semibold text-white mb-1">No pitches are now casting</h3>
                </template>
                <template v-else-if="sort === 'released'">
                    <h3 class="text-lg font-semibold text-white mb-1">No released pitches yet</h3>
                </template>
                <template v-else-if="sort === 'in_production'">
                    <h3 class="text-lg font-semibold text-white mb-1">Nothing in production yet</h3>
                </template>
                <!--<template v-else-if="sort === 'expired'">
                    <h3 class="text-lg font-semibold text-white mb-1">No expired pitches</h3>
                </template>-->
                <template v-else>
                    <h3 class="text-lg font-semibold text-white mb-1">No open pitches</h3>
                    <p class="text-gray-500 mb-6 text-sm">Be the first to submit a pitch request.</p>
                    <button
                        @click="handleSubmitPitchClick"
                        class="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
                    >
                        Submit a Pitch
                    </button>
                </template>
            </div>

            <div class="space-y-3">
                <div
                    v-for="(casting, index) in castings"
                    :key="casting.id"
                    class="relative rounded-2xl border transition-colors p-5 cursor-pointer"
                    @click="handleCastingClick($event, casting)"
                    :class="[
                        { '!mt-6': sort === 'trending' && index < 3 || casting.revised_by_admin },
                        isExpired(casting) && !showDraft(casting)
                            ? 'bg-zinc-900/30 border-white/[0.04] opacity-60 grayscale-[40%]'
                            : showDraft(casting)
                                ? 'bg-zinc-900 border-dashed border-white/[0.15] hover:border-white/[0.25]'
                            : sort === 'trending' && index === 0 ? 'bg-yellow-400/[0.05] border-yellow-400/40 hover:border-yellow-400/60' :
                              sort === 'trending' && index === 1 ? 'bg-zinc-400/[0.05] border-zinc-400/40 hover:border-zinc-400/60' :
                              sort === 'trending' && index === 2 ? 'bg-amber-600/[0.05] border-amber-600/40 hover:border-amber-600/60' :
                              'bg-zinc-900 border-white/[0.06] hover:border-white/[0.12]'
                    ]"
                >
                    <!-- Draft badge (own drafts only) -->
                    <div
                        v-if="showDraft(casting)"
                        class="absolute -top-2.5 left-4 text-[11px] font-black px-2 py-0.5 rounded-full leading-tight bg-zinc-600 text-white/70"
                    >Draft</div>

                    <!-- Rank badge (trending only, top 3 with color) -->
                    <div
                        v-if="sort === 'trending' && index < 3 && !showDraft(casting)"
                        class="absolute -top-2.5 left-4 text-[11px] font-black px-2 py-0.5 rounded-full leading-tight"
                        :class="{
                            'bg-yellow-400 text-black': index === 0,
                            'bg-zinc-300 text-black':   index === 1,
                            'bg-amber-600 text-white':  index === 2,
                        }"
                    >#{{ index + 1 }}</div>

                    <!-- Revised by Staff badge -->
                    <div
                        v-if="casting.revised_by_admin && !showDraft(casting)"
                        :class="[
                            'absolute -top-2.5 text-[11px] font-black px-2 py-0.5 rounded-full leading-tight bg-emerald-600/90 text-white border border-emerald-400/30',
                            sort === 'trending' && index < 3 ? 'left-16' : 'left-4'
                        ]"
                    >Revised by Staff for publication</div>

                    <!-- Header row: title + star count + thumbs up -->
                    <div class="flex items-start justify-between gap-3 mb-1">
                        <div class="min-w-0">
                            <h3 class="text-white text-base font-bold leading-snug">{{ casting.title || '(Untitled)' }}</h3>
                            <div class="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                                <p class="text-white/40 text-xs">
                                    by
                                    <Link
                                        :href="route(routePrefix + '.profile.my.castings', { slug: casting.requested_by })"
                                        @click="trackFathomEvent(getFathomPagePrefix(routePrefix) + '_Castings_List_Castings_Page_Clicked')"
                                        class="text-sky-400/80 hover:text-sky-300 transition-colors"
                                    >{{ casting.requested_by }}</Link>
                                </p>
                                <!--<span
                                    v-if="formatExpiry(casting.expires_at) && !isExpired(casting)"
                                    class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-white/[0.06] text-white/30"
                                >{{ formatExpiry(casting.expires_at) }}</span>
                                <span
                                    v-if="isExpired(casting)"
                                    class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400"
                                >{{ formatExpiredOn(casting) }}</span>-->
                                <span
                                    v-if="casting.status === 4 || casting.status === 8"
                                    class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-500/20 border border-orange-400/40 text-orange-300 text-[10px] font-bold shadow shadow-orange-500/10"
                                >In Production</span>
                                <span
                                    v-else-if="casting.auditions?.length"
                                    class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-[10px] font-bold shadow shadow-emerald-500/10"
                                >
                                    <span class="relative flex h-1.5 w-1.5">
                                        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                        <span class="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                                    </span>
                                    {{ casting.auditions.length }} audition{{ casting.auditions.length !== 1 ? 's' : '' }} in the race
                                </span>
                            </div>
                        </div>

                        <div class="flex items-center gap-2 flex-shrink-0">
                            <!-- Boost / Not For Me — hidden for owner -->
                            <div v-if="!casting.is_own" class="flex items-center gap-1">
                                <!-- Boost -->
                                <button
                                    @click.stop="submitVote(casting, 1)"
                                    :disabled="voteLoading[casting.id]"
                                    :class="[
                                        'flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-semibold transition-all active:scale-95',
                                        userVote(casting) === 1
                                            ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                                            : 'bg-transparent border-white/10 text-white/40 hover:border-blue-500/30 hover:text-blue-300',
                                    ]"
                                    title="Boost this pitch with Stars"
                                >
                                    <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3 h-3 flex-shrink-0" />
                                    {{ userVote(casting) === 1 ? 'Boosted' : 'Boost' }}<template v-if="userVote(casting) !== 1"><span class="opacity-50">· {{ supportCost === -1 ? '?' : supportCost }}</span></template>
                                </button>

                                <!-- Net score -->
                                <span
                                    class="text-xs font-bold tabular-nums w-6 text-center"
                                    :class="{
                                        'text-blue-400':   netScore(casting) > 0,
                                        'text-orange-400': netScore(casting) < 0,
                                        'text-white/30':   netScore(casting) === 0,
                                    }"
                                >{{ netScore(casting) }}</span>

                                <!-- Not For Me -->
                                <button
                                    @click.stop="submitVote(casting, -1)"
                                    :disabled="voteLoading[casting.id]"
                                    :class="[
                                        'flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-semibold transition-all active:scale-95',
                                        userVote(casting) === -1
                                            ? 'bg-orange-500/20 border-orange-500/40 text-orange-300'
                                            : 'bg-transparent border-white/10 text-white/40 hover:border-orange-500/30 hover:text-orange-300',
                                    ]"
                                    title="Mark this pitch as not for me"
                                >
                                    <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3 h-3 flex-shrink-0 opacity-50" />
                                    {{ userVote(casting) === -1 ? 'Not for me' : 'Not for me' }}<template v-if="userVote(casting) !== -1"><span class="opacity-50">· {{ supportCost === -1 ? '?' : supportCost }}</span></template>
                                </button>
                            </div>

                            <!-- Net score for own pitches -->
                            <span
                                v-else
                                class="text-xs font-bold tabular-nums text-center"
                                :class="{
                                    'text-blue-400':   netScore(casting) > 0,
                                    'text-orange-400': netScore(casting) < 0,
                                    'text-white/30':   netScore(casting) === 0,
                                }"
                            >
                                <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="inline w-3 h-3 mr-0.5" />{{ netScore(casting) }}
                            </span>
                        </div>
                    </div>

                    <!-- Vote error (e.g. insufficient balance) -->
                    <p v-if="voteError[casting.id]" class="text-red-400 text-xs mt-1 mb-2">{{ voteError[casting.id] }}</p>

                    <!-- Description -->
                    <p v-if="casting.description" class="text-white/60 text-sm leading-relaxed mb-3">
                        {{ expandedDescriptions[casting.id] ? casting.description : casting.description.slice(0, 200) }}{{ !expandedDescriptions[casting.id] && casting.description.length > 200 ? '… ' : ' ' }}<button
                            v-if="casting.description.length > 200"
                            @click="toggleDescription(casting.id)"
                            class="text-sky-400/80 hover:text-sky-300 text-xs font-semibold transition-colors"
                        >{{ expandedDescriptions[casting.id] ? 'Read less' : 'Read more' }}</button>
                    </p>

                    <!-- Director's Notes -->
                    <div v-if="casting.director_notes" class="mb-3 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                        <p class="text-white/30 text-[10px] font-bold uppercase tracking-wider mb-1">Director's Notes</p>
                        <p class="text-white/50 text-xs leading-relaxed line-clamp-2">{{ casting.director_notes }}</p>
                    </div>

                    <!-- Performers row -->
                    <div
                        v-if="casting.performers.length || casting.performer_description"
                        class="flex flex-wrap items-center gap-1.5 mb-2"
                    >
                        <a
                            v-for="performer in casting.performers"
                            :key="'p-' + performer.id"
                            :href="route(routePrefix + '.performer.show', { slug: performer.slug })"
                            @click="trackFathomEvent(getFathomPagePrefix(routePrefix) + '_Castings_List_Performer_Clicked')"
                            class="flex items-center gap-1.5 px-2 py-1 rounded-full bg-pink-500/20 text-pink-300 text-xs font-medium hover:bg-pink-500/30 hover:text-pink-200 transition-colors"
                        >
                            <img
                                v-if="!requiresAgeVerification"
                                :src="performer.thumbnail_url"
                                :alt="performer.name"
                                class="w-5 h-5 rounded-full object-cover object-top"
                            />
                            {{ performer.name }}
                        </a>
                        <span
                            v-if="casting.performer_description && !casting.performers.length"
                            class="px-2.5 py-1 rounded-full bg-pink-500/20 text-pink-300 text-xs font-medium italic"
                        >
                            {{ casting.performer_description }}
                        </span>
                    </div>

                    <!-- Format / Setting / Tags row -->
                    <div
                        v-if="casting.format || casting.setting?.length || casting.tags?.length"
                        class="flex flex-wrap items-center gap-1.5 mb-3"
                    >
                        <span
                            v-if="casting.format"
                            class="px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-xs font-semibold"
                        >
                            {{ formatLabel(casting.format) }}
                        </span>
                        <span
                            v-for="s in (casting.setting || [])"
                            :key="'s-' + s"
                            class="px-2.5 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-medium"
                        >
                            {{ s }}
                        </span>
                        <span
                            v-for="tag in (casting.tags || [])"
                            :key="'t-' + tag.id"
                            class="px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium"
                        >
                            {{ tag.name }}
                        </span>
                    </div>

                    <!-- Progress bar (star goal) — hidden, kept for reference (replaced by vote system) -->
                    <!--
                    <div v-if="casting.star_goal > 0" class="pt-3 border-t border-white/[0.06]">
                        <div class="flex items-center justify-between mb-1.5">
                            <span v-if="starsReceived(casting) / casting.star_goal >= 0.75" class="text-xs font-semibold text-orange-400 flex items-center gap-1">
                                🔥 Trending towards pre-production
                            </span>
                            <span v-else class="text-xs text-white/30"></span>
                            <span class="text-xs text-white/40 font-medium">
                                {{ starsReceived(casting) }} / {{ casting.star_goal }} stars
                            </span>
                        </div>
                        <div class="h-1.5 bg-white/[0.08] rounded-full overflow-hidden">
                            <div
                                class="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full transition-all duration-500"
                                :style="{ width: Math.min(100, Math.round(starsReceived(casting) / casting.star_goal * 100)) + '%' }"
                            />
                        </div>
                    </div>
                    -->
                </div>
            </div>

            <!-- Infinite scroll sentinel + loading indicator -->
            <div ref="sentinel" class="mt-4 flex justify-center h-8">
                <div v-if="loading" class="h-20 w-full flex items-center justify-center">
                    <div class="spinner">
                        <span class="text-white"></span>
                    </div>
                </div>
            </div>
        </template>
    </div>
</div>

</template>

<style scoped>
.pop-enter-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.pop-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: scale(0.95) translateY(-4px); }

.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }
@keyframes clean-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
.spinner2 {
    animation: clean-spin 1s linear infinite;
}
</style>
